export type PlayMode = "worklet" | "script";

export type LiveVoicePlayer = {
  sampleRate: number;
  mode: PlayMode;
  pushPcm: (samples: Float32Array) => void;
  setMuted: (on: boolean) => void;
  getQueueMs: () => number;
  getUnderruns: () => number;
  getOverruns: () => number;
  stop: () => void;
};

function preferScriptProcessor(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /DashcamApp/i.test(ua) || /; wv\)/.test(ua);
}

function createPlayContext(): AudioContext {
  // Prefer native 8 kHz like the Android AudioTrack path — skips upsample and
  // matches mobile clarity. Browsers that ignore sampleRate fall back to default.
  try {
    return new AudioContext({ latencyHint: "interactive", sampleRate: 8000 });
  } catch {
    try {
      return new AudioContext({ latencyHint: "interactive" });
    } catch {
      return new AudioContext();
    }
  }
}

function attachResume(ctx: AudioContext): () => void {
  const resume = () => {
    if (document.visibilityState === "visible" && ctx.state === "suspended") void ctx.resume();
  };
  document.addEventListener("visibilitychange", resume);
  window.addEventListener("pageshow", resume);
  window.addEventListener("dashcam:app-resume", resume);
  return () => {
    document.removeEventListener("visibilitychange", resume);
    window.removeEventListener("pageshow", resume);
    window.removeEventListener("dashcam:app-resume", resume);
  };
}

const PLAY_SRC = `
class PcmPlayProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.cap = Math.max(4096, Math.floor(sampleRate * 0.3));
    this.buf = new Float32Array(this.cap);
    this.r = 0;
    this.w = 0;
    this.n = 0;
    this.muted = false;
    this.started = false;
    this.last = 0;
    this.hold = 0;
    this.underrun = 0;
    this.overrun = 0;
    // Keep a little more than mobile's 3×20ms frames so web JS jitter does not
    // punch holes, but still under ~100ms for low delay.
    this.maxKeep = Math.floor(sampleRate * 0.1);
    this.target = Math.floor(sampleRate * 0.05);
    this.minStart = Math.floor(sampleRate * 0.025);
    this.tick = 0;
    this.port.onmessage = (ev) => {
      const d = ev.data;
      if (d && d.type === "mute") {
        this.muted = !!d.on;
        return;
      }
      const s = d && d.samples;
      if (!s || !s.length) return;
      for (let i = 0; i < s.length; i++) {
        this.buf[this.w] = s[i];
        this.w = (this.w + 1) % this.cap;
        if (this.n < this.cap) this.n++;
        else this.r = (this.r + 1) % this.cap;
      }
      if (this.n > this.maxKeep) {
        const drop = this.n - this.target;
        this.r = (this.r + drop) % this.cap;
        this.n = this.target;
        this.overrun++;
      }
    };
  }
  pull() {
    if (this.n <= 0) return null;
    const v = this.buf[this.r];
    this.r = (this.r + 1) % this.cap;
    this.n--;
    return v;
  }
  process(_inputs, outputs) {
    const chans = outputs[0];
    const out = chans && chans[0];
    if (!out) return true;
    if (this.muted) {
      for (let i = 0; i < out.length; i++) this.pull();
      for (let c = 0; c < chans.length; c++) chans[c].fill(0);
      return true;
    }
    if (!this.started) {
      if (this.n < this.minStart) {
        for (let c = 0; c < chans.length; c++) chans[c].fill(0);
        return true;
      }
      this.started = true;
    }
    const holdMax = Math.floor(sampleRate * 0.06);
    for (let i = 0; i < out.length; i++) {
      let v = this.pull();
      if (v == null) {
        this.hold++;
        if (this.hold === 1) this.underrun++;
        // Hold last sample briefly instead of hard zero (clicks / "unclear" holes).
        v = this.hold < 8 ? this.last * (1 - this.hold / 8) : 0;
        if (this.hold > holdMax) {
          this.started = false;
          this.last = 0;
        }
      } else {
        this.hold = 0;
        this.last = v;
      }
      for (let c = 0; c < chans.length; c++) chans[c][i] = v;
    }
    this.tick++;
    if ((this.tick & 31) === 0) {
      this.port.postMessage({ type: "stats", n: this.n, underrun: this.underrun, overrun: this.overrun });
    }
    return true;
  }
}
registerProcessor("pcm-play", PcmPlayProcessor);
`;

function workletUrl(): string {
  return URL.createObjectURL(new Blob([PLAY_SRC], { type: "application/javascript" }));
}

type Sink = {
  sampleRate: number;
  mode: PlayMode;
  push: (samples: Float32Array) => void;
  setMuted: (on: boolean) => void;
  queueMs: () => number;
  underruns: () => number;
  overruns: () => number;
  stop: () => void;
};

function scriptProcessorSink(ctx: AudioContext, muteGain: GainNode): Sink {
  const Ctor = (
    ctx as AudioContext & {
      createScriptProcessor?: (bs: number, i: number, o: number) => ScriptProcessorNode;
    }
  ).createScriptProcessor;
  if (!Ctor) throw new Error("Audio playback is not supported in this browser.");
  const cap = Math.max(4096, Math.floor(ctx.sampleRate * 0.3));
  const buf = new Float32Array(cap);
  let r = 0;
  let w = 0;
  let n = 0;
  let muted = false;
  let started = false;
  let last = 0;
  let hold = 0;
  let underrun = 0;
  let overrun = 0;
  const maxKeep = Math.floor(ctx.sampleRate * 0.1);
  const target = Math.floor(ctx.sampleRate * 0.05);
  const minStart = Math.floor(ctx.sampleRate * 0.025);
  const holdMax = Math.floor(ctx.sampleRate * 0.06);
  const proc = Ctor.call(ctx, 256, 1, 1);
  proc.onaudioprocess = (ev) => {
    const out = ev.outputBuffer.getChannelData(0);
    if (muted) {
      for (let i = 0; i < out.length; i++) {
        if (n > 0) {
          r = (r + 1) % cap;
          n--;
        }
      }
      out.fill(0);
      return;
    }
    if (!started) {
      if (n < minStart) {
        out.fill(0);
        return;
      }
      started = true;
    }
    for (let i = 0; i < out.length; i++) {
      if (n > 0) {
        last = buf[r]!;
        r = (r + 1) % cap;
        n--;
        hold = 0;
        out[i] = last;
      } else {
        hold++;
        if (hold === 1) underrun++;
        out[i] = hold < 8 ? last * (1 - hold / 8) : 0;
        if (hold > holdMax) {
          started = false;
          last = 0;
        }
      }
    }
  };
  proc.connect(muteGain);
  return {
    sampleRate: ctx.sampleRate,
    mode: "script",
    push(samples: Float32Array) {
      if (!samples.length) return;
      for (let i = 0; i < samples.length; i++) {
        buf[w] = samples[i]!;
        w = (w + 1) % cap;
        if (n < cap) n++;
        else r = (r + 1) % cap;
      }
      if (n > maxKeep) {
        const drop = n - target;
        r = (r + drop) % cap;
        n = target;
        overrun++;
      }
    },
    setMuted(on: boolean) {
      muted = on;
      muteGain.gain.value = on ? 0 : 1;
    },
    queueMs: () => (n / ctx.sampleRate) * 1000,
    underruns: () => underrun,
    overruns: () => overrun,
    stop() {
      proc.onaudioprocess = null;
      proc.disconnect();
    },
  };
}

async function workletSink(ctx: AudioContext, muteGain: GainNode): Promise<Sink> {
  const url = workletUrl();
  await ctx.audioWorklet.addModule(url);
  URL.revokeObjectURL(url);
  const node = new AudioWorkletNode(ctx, "pcm-play", {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    channelCount: 1,
    channelCountMode: "explicit",
  });
  let n = 0;
  let underrun = 0;
  let overrun = 0;
  node.port.onmessage = (ev) => {
    const d = ev.data;
    if (!d || d.type !== "stats") return;
    n = Number(d.n) || 0;
    underrun = Number(d.underrun) || 0;
    overrun = Number(d.overrun) || 0;
  };
  node.connect(muteGain);
  return {
    sampleRate: ctx.sampleRate,
    mode: "worklet",
    push(samples: Float32Array) {
      if (!samples.length) return;
      const copy = new Float32Array(samples);
      node.port.postMessage({ samples: copy }, [copy.buffer]);
    },
    setMuted(on: boolean) {
      muteGain.gain.value = on ? 0 : 1;
      node.port.postMessage({ type: "mute", on });
    },
    queueMs: () => (n / ctx.sampleRate) * 1000,
    underruns: () => underrun,
    overruns: () => overrun,
    stop() {
      try {
        node.disconnect();
      } catch {
        /* ignore */
      }
    },
  };
}

export async function startLiveVoicePlayer(): Promise<LiveVoicePlayer> {
  const ctx = createPlayContext();
  await ctx.resume();
  const detach = attachResume(ctx);
  const muteGain = ctx.createGain();
  muteGain.gain.value = 1;
  muteGain.connect(ctx.destination);
  let sink: Sink;
  if (preferScriptProcessor()) {
    sink = scriptProcessorSink(ctx, muteGain);
  } else {
    try {
      sink = await workletSink(ctx, muteGain);
    } catch {
      sink = scriptProcessorSink(ctx, muteGain);
    }
  }
  return {
    sampleRate: sink.sampleRate,
    mode: sink.mode,
    pushPcm(samples: Float32Array) {
      if (!samples.length) return;
      sink.push(samples);
    },
    setMuted(on: boolean) {
      sink.setMuted(on);
    },
    getQueueMs: () => sink.queueMs(),
    getUnderruns: () => sink.underruns(),
    getOverruns: () => sink.overruns(),
    stop() {
      detach();
      sink.stop();
      try {
        muteGain.disconnect();
      } catch {
        /* ignore */
      }
      if (ctx.state !== "closed") void ctx.close();
    },
  };
}
